import {
  codeableConceptMatchesToken,
  createReference,
  MedplumClient,
  resolveId,
} from "@medplum/core";
import { Encounter, Patient, Reference, RelatedPerson } from "@medplum/fhirtypes";

import { getEncounterServiceDateTimezone } from "@/encounters/utils/encounter-timezone";
import { Extensions } from "@/fhir/enums/extensions";
import {
  BirthingComponent,
  LaborFlowsheetForms,
  NewbornPatientLinkingIdentifierSystems,
} from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";
import { formatHumanOfficialName, joinReferenceString, joinTokenString } from "@/utils/medplum";
import { fetchAllPaginatedResources } from "@/utils/pagination";

interface NewbornCandidate {
  relatedPerson: RelatedPerson;
  newbornPatientId: string;
  sourceQuestionnaireResponseId: string | undefined;
}

function getSourceQuestionnaireResponseId(relatedPerson: RelatedPerson): string | undefined {
  return relatedPerson.identifier?.find(
    (identifier) =>
      identifier.system ===
      NewbornPatientLinkingIdentifierSystems.NewbornSourceQuestionnaireResponse,
  )?.value;
}

/**
 * Resolves a `RelatedPerson(CHILD)`'s newborn `Patient` id from its baby-reference extension. The
 * newborn-creation bot always sets this extension, but a `RelatedPerson` missing it, carrying an
 * unresolvable reference, or carrying a reference to something other than a `Patient` is skipped
 * rather than failing the whole picker over one bad record. The resourceType check matters:
 * `resolveId` only splits on `/` and returns the second segment regardless of resourceType, so an
 * extension pointing at, say, `Observation/newborn-1` would otherwise resolve to the same bare id
 * as an unrelated `Patient/newborn-1` and misattribute that Patient as this mother's newborn.
 */
function resolveNewbornPatientId(relatedPerson: RelatedPerson): string | undefined {
  const babyReferenceExtension = relatedPerson.extension?.find(
    (extension) => extension.url === Extensions.ArtifactRelatedArtifact,
  );
  const reference = babyReferenceExtension?.valueReference?.reference;
  if (!reference?.startsWith("Patient/")) {
    return undefined;
  }
  return resolveId(babyReferenceExtension?.valueReference);
}

function toNewbornCandidates(relatedPersons: RelatedPerson[]): NewbornCandidate[] {
  return relatedPersons.reduce<NewbornCandidate[]>((candidates, relatedPerson) => {
    const newbornPatientId = resolveNewbornPatientId(relatedPerson);
    if (newbornPatientId) {
      candidates.push({
        relatedPerson,
        newbornPatientId,
        sourceQuestionnaireResponseId: getSourceQuestionnaireResponseId(relatedPerson),
      });
    }
    return candidates;
  }, []);
}

/**
 * Narrows newborn candidates to the ones whose source Birthing `QuestionnaireResponse` belongs to
 * `laborEncounterId` — what a delivery-scoped auto-select needs. A candidate whose source
 * `QuestionnaireResponse` can't be resolved is excluded rather than guessed into either side.
 */
async function filterCandidatesByLaborEncounter(
  medplum: MedplumClient,
  candidates: NewbornCandidate[],
  laborEncounterId: string,
  practiceCompartment: string,
): Promise<NewbornCandidate[]> {
  const matchesLaborEncounter = await Promise.all(
    candidates.map(async (candidate) => {
      if (!candidate.sourceQuestionnaireResponseId) {
        return false;
      }
      const questionnaireResponse = await medplum.searchOne(
        "QuestionnaireResponse",
        {
          _compartment: practiceCompartment,
          _id: candidate.sourceQuestionnaireResponseId,
        },
        { cache: "no-cache" },
      );
      return resolveId(questionnaireResponse?.encounter) === laborEncounterId;
    }),
  );

  return candidates.filter((_candidate, index) => matchesLaborEncounter[index]);
}

interface ResolvedBirthTiming {
  birthDateTime: string;
  /**
   * The IANA timezone the delivery was charted in, read off the Labor encounter's own timezone
   * extension — the same value the newborn-creation bot already uses to extract `Patient.birthDate`.
   * Undefined when the Labor encounter can't be resolved or carries no timezone extension; a caller
   * showing `birthDateTime` without it should show a bare date rather than guess a zone.
   */
  deliveryTimezone: string | undefined;
}

/**
 * The Labor encounter's own stored timezone, so a caller can render `birthDateTime` in the same
 * zone `Patient.birthDate` was already extracted in — never the viewer's browser timezone, which
 * could disagree with `birthDate` and make the two look inconsistent side by side. Returns
 * undefined rather than throwing when the Encounter can't be resolved.
 */
async function resolveDeliveryTimezone(
  medplum: MedplumClient,
  encounterReference: Reference<Encounter> | undefined,
  practiceCompartment: string,
): Promise<string | undefined> {
  const encounterId = resolveId(encounterReference);
  if (!encounterId) {
    return undefined;
  }

  try {
    const encounter = await medplum.searchOne("Encounter", {
      _compartment: practiceCompartment,
      _id: encounterId,
    });
    return getEncounterServiceDateTimezone(encounter)?.code;
  } catch {
    return undefined;
  }
}

/**
 * The newborn's actual body-delivered timestamp and the delivery's own timezone, read off its
 * source Birthing `QuestionnaireResponse`'s Observation. `birthDateTime` is a full `dateTime`,
 * unlike `Patient.birthDate` (a FHIR date, already truncated to day-precision) — the only value
 * that can tell twins born the same calendar day apart. Excludes `cancelled` and `entered-in-error`
 * Observations, both void statuses per FHIR — an entered-in-error record should be treated as
 * though it never existed. Returns undefined when the source QR is missing, its Birthing
 * Observation can't be found, or the search itself fails — this runs inside a `Promise.all` over
 * every newborn, so one newborn's lookup failure must degrade to a missing birth time rather than
 * reject the whole batch and leave the picker showing none of them.
 */
async function resolveBirthTiming(
  medplum: MedplumClient,
  sourceQuestionnaireResponseId: string | undefined,
  practiceCompartment: string,
): Promise<ResolvedBirthTiming | undefined> {
  if (!sourceQuestionnaireResponseId) {
    return undefined;
  }

  let birthingObservation;
  try {
    birthingObservation = await medplum.searchOne("Observation", {
      _compartment: practiceCompartment,
      "derived-from": joinReferenceString("QuestionnaireResponse", sourceQuestionnaireResponseId),
      code: joinTokenString(Systems.QH, LaborFlowsheetForms.Birthing),
      "status:not": "cancelled,entered-in-error",
    });
  } catch {
    return undefined;
  }

  const bodyDeliveredComponent = birthingObservation?.component?.find((component) =>
    codeableConceptMatchesToken(component.code ?? {}, BirthingComponent.BodyDeliveredDateTime),
  );
  const birthDateTime = bodyDeliveredComponent?.valueDateTime;
  if (!birthDateTime) {
    return undefined;
  }

  const deliveryTimezone = await resolveDeliveryTimezone(
    medplum,
    birthingObservation?.encounter,
    practiceCompartment,
  );
  return { birthDateTime, deliveryTimezone };
}

async function toRelatedNewborn(
  medplum: MedplumClient,
  patient: Patient,
  sourceQuestionnaireResponseId: string | undefined,
  practiceCompartment: string,
): Promise<RelatedNewborn | undefined> {
  if (!patient.id) {
    return undefined;
  }
  const birthTiming = await resolveBirthTiming(
    medplum,
    sourceQuestionnaireResponseId,
    practiceCompartment,
  );
  return {
    patientReference: createReference(patient),
    patientId: patient.id,
    birthDate: patient.birthDate,
    birthDateTime: birthTiming?.birthDateTime,
    deliveryTimezone: birthTiming?.deliveryTimezone,
    name: formatHumanOfficialName(patient.name) || undefined,
  };
}

export interface RelatedNewborn {
  /** Reference to the discovered newborn `Patient`. */
  patientReference: Reference<Patient>;
  /** The newborn `Patient`'s id, for direct lookups without re-parsing the reference. */
  patientId: string;
  /**
   * The newborn's date of birth (day-precision only). Falls back for labeling when
   * `birthDateTime` can't be resolved. Undefined if not yet set.
   */
  birthDate: string | undefined;
  /**
   * The newborn's actual body-delivered timestamp, precise enough to tell same-day twins apart in
   * the picker. Render it alongside `birthDate`, in `deliveryTimezone`, rather than in place of
   * `birthDate` — converting it to the viewer's own timezone could show a different calendar day
   * than `birthDate` does.
   */
  birthDateTime: string | undefined;
  /**
   * The IANA timezone `birthDateTime` should be rendered in — the same zone `birthDate` was already
   * extracted in. Undefined whenever `birthDateTime` itself is undefined, or when the delivery's
   * Labor encounter carries no timezone extension.
   */
  deliveryTimezone: string | undefined;
  /** The newborn's formatted name, once Birth Details has set one. Undefined until then. */
  name: string | undefined;
}

export interface GetRelatedNewbornsForMotherOptions {
  /**
   * Scopes results to newborns whose source Birthing `QuestionnaireResponse` belongs to this Labor
   * encounter — what a delivery-scoped auto-select uses. Omitted for the standalone picker, which
   * shows every newborn discovered across the mother's lifetime and lets the person picking
   * disambiguate by name and birth date/time.
   */
  laborEncounterId?: string;
}

/**
 * Discovers a mother's newborns entirely via `RelatedPerson(CHILD)`: searches for her
 * `RelatedPerson(CHILD)` records, resolves each one's baby-reference extension to a newborn
 * `Patient` id, then batch-reads those Patients. Scoped to `practiceOrganizationId`'s compartment on
 * every search, per this app's tenant model — an unscoped search reading practice data is a
 * cross-tenant PHI leak regardless of whether the query shape happens to already be narrow.
 */
export async function getRelatedNewbornsForMother(
  medplum: MedplumClient,
  motherReference: Reference<Patient>,
  practiceOrganizationId: string,
  options: GetRelatedNewbornsForMotherOptions = {},
): Promise<RelatedNewborn[]> {
  const motherId = resolveId(motherReference);
  if (!motherId) {
    return [];
  }

  const practiceCompartment = joinReferenceString("Organization", practiceOrganizationId);

  // Paginated: RelatedPerson(CHILD) discovery accumulates across the mother's whole lifetime
  // (multiple pregnancies, twins), so a single unpaginated page could silently drop older newborns.
  const relatedPersonsResult = await fetchAllPaginatedResources<RelatedPerson>(
    medplum,
    "RelatedPerson",
    {
      _compartment: practiceCompartment,
      patient: joinReferenceString("Patient", motherId),
      relationship: "CHILD",
    },
  );
  if (!relatedPersonsResult.success) {
    throw relatedPersonsResult.error;
  }
  const relatedPersons = relatedPersonsResult.data;

  let candidates = toNewbornCandidates(relatedPersons);
  if (options.laborEncounterId) {
    candidates = await filterCandidatesByLaborEncounter(
      medplum,
      candidates,
      options.laborEncounterId,
      practiceCompartment,
    );
  }

  if (candidates.length === 0) {
    return [];
  }

  // Paginated for the same reason as the RelatedPerson search above: a mother can accumulate more
  // newborn candidates over her lifetime than fit on one page.
  const newbornPatientIds = candidates.map((candidate) => candidate.newbornPatientId);
  const newbornPatientsResult = await fetchAllPaginatedResources<Patient>(medplum, "Patient", {
    _compartment: practiceCompartment,
    _id: newbornPatientIds.join(","),
  });
  if (!newbornPatientsResult.success) {
    throw newbornPatientsResult.error;
  }
  const newbornPatients = newbornPatientsResult.data;

  // Last one wins if two RelatedPerson(CHILD) records ever pointed at the same newborn Patient id
  // (bad/duplicate data, not an expected shape) -- the Patient itself is never misattributed, only
  // which RelatedPerson's own source QR ends up supplying its birth time.
  const candidateByNewbornPatientId = new Map(
    candidates.map((candidate) => [candidate.newbornPatientId, candidate]),
  );

  const relatedNewborns = await Promise.all(
    newbornPatients.map((patient) =>
      toRelatedNewborn(
        medplum,
        patient,
        patient.id
          ? candidateByNewbornPatientId.get(patient.id)?.sourceQuestionnaireResponseId
          : undefined,
        practiceCompartment,
      ),
    ),
  );

  return relatedNewborns.filter((relatedNewborn) => relatedNewborn !== undefined);
}
